// UTC-correct calendar math — mirrors the backend's "universal UTC, day-granular"
// model so day cells and the past-post warning line up exactly with how slots
// were planned. We deliberately work in UTC components (getUTC*) rather than
// local time to avoid off-by-one day drift.
//
// v2 has no time-of-day: a slot is stored at 00:00 UTC and the day IS the
// schedule, so there are no time helpers here and no time picker in the UI.

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/** Sunday-first, matching Date.getUTCDay(). */
export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export interface YearMonth {
  year: number;
  /** 1-12 */
  month: number;
}

/** Number of days in the given UTC month (month is 1-12). */
export function daysInUtcMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Weekday (0=Sun..6=Sat) of the 1st of the month. */
function firstWeekday(year: number, month: number): number {
  return new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
}

/**
 * A month laid out as calendar weeks. Each cell is the day-of-month number or
 * null for the leading/trailing padding days. Always a whole number of weeks.
 */
export function buildMonthCells(year: number, month: number): (number | null)[] {
  const total = daysInUtcMonth(year, month);
  const lead = firstWeekday(year, month);
  const cells: (number | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export function monthLabel({ year, month }: YearMonth): string {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

export function addMonths({ year, month }: YearMonth, delta: number): YearMonth {
  const zeroBased = month - 1 + delta;
  return {
    year: year + Math.floor(zeroBased / 12),
    month: ((zeroBased % 12) + 12) % 12 + 1,
  };
}

export function currentYearMonth(): YearMonth {
  const now = new Date();
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
}

export function isCurrentMonth({ year, month }: YearMonth): boolean {
  const now = currentYearMonth();
  return year === now.year && month === now.month;
}

// --- Slot ↔ day helpers (all UTC) ---

/** The UTC day-of-month a slot falls on. */
export function slotUtcDay(scheduledAt: string): number {
  return new Date(scheduledAt).getUTCDate();
}

export function slotInMonth(scheduledAt: string, year: number, month: number): boolean {
  const d = new Date(scheduledAt);
  return d.getUTCFullYear() === year && d.getUTCMonth() + 1 === month;
}

/**
 * "Tuesday, June 9, 2026" from a slot's scheduled day.
 *
 * v2 is day-granular — slots are stored at 00:00 UTC and carry no time of day,
 * so there is deliberately no time in this label and no time picker anywhere.
 */
export function formatSlotSchedule(scheduledAt: string): string {
  const d = new Date(scheduledAt);
  return `${WEEKDAY_NAMES[d.getUTCDay()]}, ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** "9 Jun" — the compact form for grid cells and list rows. */
export function formatSlotDayShort(scheduledAt: string): string {
  const d = new Date(scheduledAt);
  return `${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()].slice(0, 3)}`;
}

/** Build the UTC ISO day for a day-of-month, clamped to the month's length. */
export function toUtcIsoDay(year: number, month: number, day: number): string {
  const clampedDay = Math.min(Math.max(day, 1), daysInUtcMonth(year, month));
  return new Date(Date.UTC(year, month - 1, clampedDay)).toISOString();
}

/** "2m 30s" / "45s" — a video idea's runtime. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

// --- "Now" comparisons (UTC, day-granular) ---

function utcMidnightMs(year: number, month: number, day: number): number {
  return Date.UTC(year, month - 1, day);
}

export function isTodayUtc(year: number, month: number, day: number): boolean {
  const now = new Date();
  return (
    now.getUTCFullYear() === year &&
    now.getUTCMonth() + 1 === month &&
    now.getUTCDate() === day
  );
}

/** A day is "past" once the end of that UTC day has elapsed. */
export function isPastDayUtc(year: number, month: number, day: number): boolean {
  return Date.now() >= utcMidnightMs(year, month, day + 1);
}

/** Mirrors the backend rule: past once the end of the seat's scheduled UTC day passed. */
export function isSlotPast(scheduledAt: string): boolean {
  const d = new Date(scheduledAt);
  const endOfDay = Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate() + 1,
  );
  return Date.now() >= endOfDay;
}

/** Is (year, month) strictly before the current UTC month? (archived-ish) */
export function isMonthInPast(year: number, month: number): boolean {
  const now = new Date();
  const nowY = now.getUTCFullYear();
  const nowM = now.getUTCMonth() + 1;
  return year < nowY || (year === nowY && month < nowM);
}

/**
 * How many slots a month should get in TOTAL. Mirrors backend
 * `slotCountForMonth`: future months take the full cadence; the current month is
 * pro-rated by days left.
 *
 * `postsPerMonth` is the whole month's count — the format mix summed, via
 * `totalPostsPerMonth`. The per-bucket split of that total is a server concern
 * (the plan carries its own targets); nothing in the UI needs to predict it.
 */
export function slotCountForMonth(
  year: number,
  month: number,
  postsPerMonth: number,
  now: Date = new Date(),
): number {
  const total = daysInUtcMonth(year, month);
  const isCurrent =
    year === now.getUTCFullYear() && month === now.getUTCMonth() + 1;
  const first = isCurrent ? now.getUTCDate() : 1;
  if (first <= 1) return Math.max(1, postsPerMonth);
  const remaining = total - first + 1;
  const scaled = Math.round((postsPerMonth * remaining) / total);
  return Math.min(Math.max(scaled, 1), Math.max(1, postsPerMonth));
}
